/**
 * PracticalSheetsListPage — Phase C édito (2026-09-21)
 * Liste back-office des fiches pratiques. Toutes statuts, filtre par voyage.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  listPracticalSheetsAdmin,
  deletePracticalSheet,
  createPracticalSheet,
} from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import {
  BookMarked, Loader2, Plus, Pencil, Trash2, ExternalLink, MapPin, Upload,
} from "lucide-react";
import ImportSheetDialog from "../components/admin/ImportSheetDialog";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return String(iso); }
}

export default function PracticalSheetsListPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [title, setTitle] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listPracticalSheetsAdmin({ limit: 200 });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error("Impossible de charger les fiches : " + (e?.response?.data?.detail || e?.message || "erreur"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitCreate = async () => {
    if (!title.trim() || title.trim().length < 2) {
      toast.error("Le titre doit contenir au moins 2 caractères.");
      return;
    }
    setCreating(true);
    try {
      const r = await createPracticalSheet({ title: title.trim(), status: "draft" });
      toast.success("Fiche créée.");
      setOpenCreate(false);
      setTitle("");
      await load();
      // Navigation vers l'éditeur
      window.location.href = `/dashboard/fiches-pratiques/${r.data.id}`;
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e?.message || "unknown"));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Supprimer la fiche « ${row.title || row.slug} » ?`)) return;
    try {
      await deletePracticalSheet(row.id);
      toast.success("Fiche supprimée.");
      await load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e?.message || "unknown"));
    }
  };

  return (
    <div data-testid="practical-sheets-list-page" className="max-w-5xl">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl text-primary flex items-center gap-3">
            <BookMarked className="h-7 w-7" />
            Fiches pratiques
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Fiches courtes réutilisables : lieu, horaires, tarifs, incontournables.
            Le PDF A4 réservé aux adhérents est généré uniquement pour les fiches validées.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={() => setOpenImport(true)}
            data-testid="practical-sheet-import-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Importer une fiche
          </Button>
          <Button
            variant="outline"
            onClick={() => setOpenCreate(true)}
            data-testid="practical-sheet-create-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle fiche
          </Button>
        </div>
      </header>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            Aucune fiche pour l'instant. Créez-en une pour organiser vos infos pratiques.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="practical-sheets-table">
            <thead className="bg-secondary/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-6 py-3">Titre</th>
                <th className="text-left px-6 py-3">Lieu</th>
                <th className="text-left px-6 py-3">Statut</th>
                <th className="text-left px-6 py-3">Vérifiée</th>
                <th className="text-left px-6 py-3">MAJ</th>
                <th className="text-right px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-t border-border" data-testid={`practical-sheet-row-${row.id}`}>
                  <td className="px-6 py-3 font-medium">{row.title || <em className="text-muted-foreground">Sans titre</em>}</td>
                  <td className="px-6 py-3 text-muted-foreground">
                    {row.location_name ? (
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {row.location_name}</span>
                    ) : "—"}
                  </td>
                  <td className="px-6 py-3">
                    <span className={`inline-flex text-xs px-2 py-0.5 rounded-full ${row.status === "validated" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                      {row.status === "validated" ? "Validée" : "Brouillon"}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-muted-foreground text-xs">{fmtDate(row.last_verified)}</td>
                  <td className="px-6 py-3 text-muted-foreground text-xs">{fmtDate(row.updated_at)}</td>
                  <td className="px-6 py-3 text-right">
                    <Button variant="ghost" size="sm" asChild data-testid={`practical-sheet-edit-${row.id}`}>
                      <Link to={`/dashboard/fiches-pratiques/${row.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Link>
                    </Button>
                    {row.status === "validated" && row.slug && (
                      <Button variant="ghost" size="sm" asChild data-testid={`practical-sheet-view-${row.id}`}>
                        <a href={`/fiche-pratique/${row.slug}`} target="_blank" rel="noreferrer">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => remove(row)} data-testid={`practical-sheet-delete-${row.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {total > 0 && (
          <div className="p-3 text-xs text-muted-foreground border-t border-border">
            {items.length} sur {total}
          </div>
        )}
      </div>

      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent data-testid="practical-sheet-create-dialog">          <DialogHeader>
            <DialogTitle>Nouvelle fiche pratique</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Titre *</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex. Escale à La Valette — Malte"
                data-testid="practical-sheet-input-title"
                autoFocus
              />
              <p className="text-xs text-muted-foreground mt-1">
                Vous pourrez tout compléter dans l'éditeur (adresse, horaires, incontournables…).
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)} disabled={creating}>Annuler</Button>
            <Button onClick={submitCreate} disabled={creating} data-testid="practical-sheet-create-submit">
              {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Créer la fiche
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportSheetDialog
        open={openImport}
        onOpenChange={setOpenImport}
        onImportDone={() => { setOpenImport(false); load(); }}
      />
    </div>
  );
}
